/**
 * Prompt Improvement Service
 * Uses AI to improve prompts based on available context.
 * Includes model-specific prompting guidance distilled from the
 * Multimodal Prompting Playbook (docs/deep-research-report.md).
 */

import openAIService from './OpenAIService';
import logger from '../utils/logger';

interface PromptImprovementContext {
  /** The original prompt to improve */
  originalPrompt: string;
  /** Type of content being generated */
  contentType: 'image' | 'video' | 'text';
  /** The selected AI model id (e.g. 'gemini-nano-banana', 'flux-2-max', 'veo-3.1-fast') */
  selectedModel?: string;
  /** Any upstream context (text from connected nodes) */
  upstreamContext?: string;
  /** Image context description if available */
  imageContext?: string;
  /** Actual image URLs from connected nodes for visual analysis */
  imageUrls?: string[];
  /** Surrounding text context when improving a selection (full prompt the selection is part of) */
  surroundingContext?: string;
  /** Current node parameter values (duration, aspectRatio, resolution, quality, etc.) */
  nodeParameters?: Record<string, any>;
  /**
   * Extra model best-practices text from the server knowledge base
   * (server/knowledge). Appended to the built-in model tips when provided.
   */
  modelBestPractices?: string;
}

interface PromptImprovementResult {
  improvedPrompt: string;
  success: boolean;
  error?: string;
}

// ---------------------------------------------------------------------------
// Model-specific prompting tips (distilled from docs/deep-research-report.md)
// Only models available on the platform are included.
// ---------------------------------------------------------------------------

const IMAGE_MODEL_TIPS: Record<string, string> = {
  // GPT Image models
  'gpt-image-1': `Model-specific tips for GPT Image 1:
- Supports very long prompts (up to 32 000 chars). Be detailed.
- Use structured prompts: Subject → Setting → Composition → Lighting → Camera → Constraints.
- State explicit constraints ("no extra text," "no trademarks," "no watermark").
- quality="high" is especially useful for photoreal textures and dense layouts.
- Iterative refinement works well — add/remove details in subsequent edits.`,

  'gpt-image-1.5': `Model-specific tips for GPT Image 1.5:
- Supports very long prompts (up to 32 000 chars). Be detailed.
- Use structured prompts: Subject → Setting → Composition → Lighting → Camera → Constraints.
- State explicit constraints ("no extra text," "no trademarks," "no watermark").
- quality="high" is especially useful for photoreal textures and dense layouts.
- Iterative refinement works well — add/remove details in subsequent edits.
- Supports image inputs for editing — describe what to change, not the whole scene.`,

  // Gemini / Imagen (Nano Banana family)
  'gemini-nano-banana': `Model-specific tips for Gemini Nano Banana (Google AI):
- Natively multimodal: can compose text + image in one pass.
- Good at in-image text rendering (posters, diagrams, ad layouts).
- Supports conversational/iterative editing — refine by describing changes.
- Be concrete about subject, lighting, and composition.
- Generated images include SynthID watermark.`,

  'gemini-3-pro-image-preview': `Model-specific tips for Gemini 3 Pro Image (Google AI):
- Natively multimodal with strong iterative refinement and multi-image composition.
- Excellent in-image text rendering for posters, menus, and infographics.
- Supports conversational editing — describe what to change specifically.
- Be concrete about subject, lighting, and composition.
- Generated images include SynthID watermark.`,

  // FLUX models
  'flux-2-max': `Model-specific tips for FLUX 2 Max:
- Best prompt structure: Subject + Action + Style + Context.
- Strong prompt-following — what you write is what you get.
- Great for commercial visuals with precise structure.
- Be descriptive and specific; avoid vague adjectives.
- Highest quality FLUX model — use for hero assets.`,

  'flux-2-pro': `Model-specific tips for FLUX 2 Pro:
- Best prompt structure: Subject + Action + Style + Context.
- BFL mid-tier — strong quality with multi-reference editing.
- Supports up to 9 reference images via @-syntax (e.g. "@image1 wearing the outfit from @image2").
- Great for sequential edits, style transfer, and consistent character work.
- Be descriptive and specific; avoid vague adjectives.`,

  'flux-2-turbo': `Model-specific tips for FLUX 2 Turbo:
- Best prompt structure: Subject + Action + Style + Context.
- Fast generation with good quality — ideal for iteration.
- Strong prompt-following — specify exactly what you want.
- Good balance of speed and detail for commercial work.`,

  'flux-2-flash': `Model-specific tips for FLUX 2 Flash:
- Best prompt structure: Subject + Action + Style + Context.
- Fastest FLUX model — great for rapid exploration and drafts.
- Keep prompts clear and direct; less detail tolerance than Max.
- Use for quick concept validation before refining with Max.`,
};

const VIDEO_MODEL_TIPS: Record<string, string> = {
  // Veo 3.1
  'veo-3.1-fast': `Model-specific tips for Veo 3.1:
- Generates native audio: ambient sound, SFX, and dialogue.
- Put dialogue in quotes and describe SFX timed to actions.
- Use time-coded shot blocks for multi-beat sequences.
- Camera movements: specify push-in, pan, dolly, handheld, etc.
- Combine with image-to-video for style-frame workflows.
- DO NOT try to control resolution/duration in prose — those are set via parameters.
- For UGC realism: name the environment specifically with blurred background details ("cozy gaming room, blurred Warcraft posters"), add directional window light ("dramatic natural side lighting from a window creates deep shadows and high contrast"), finish with warm color grading. This formula is the single biggest factor in making AI video look filmed.`,

  // Veo 3.1 (Google direct model IDs)
  'veo-3.1-fast-generate-preview': `Model-specific tips for Veo 3.1:
- Generates native audio: ambient sound, SFX, and dialogue.
- Put dialogue in quotes and describe SFX timed to actions.
- Use time-coded shot blocks for multi-beat sequences.
- Camera movements: specify push-in, pan, dolly, handheld, etc.
- Combine with image-to-video for style-frame workflows.
- DO NOT try to control resolution/duration in prose — those are set via parameters.
- For UGC realism: name the environment specifically with blurred background details ("cozy gaming room, blurred Warcraft posters"), add directional window light ("dramatic natural side lighting from a window creates deep shadows and high contrast"), finish with warm color grading. This formula is the single biggest factor in making AI video look filmed.`,

  'veo-3.1-generate-preview': `Model-specific tips for Veo 3.1:
- Generates native audio: ambient sound, SFX, and dialogue.
- Put dialogue in quotes and describe SFX timed to actions.
- Use time-coded shot blocks for multi-beat sequences.
- Camera movements: specify push-in, pan, dolly, handheld, etc.
- Combine with image-to-video for style-frame workflows.
- DO NOT try to control resolution/duration in prose — those are set via parameters.
- For UGC realism: name the environment specifically with blurred background details ("cozy gaming room, blurred Warcraft posters"), add directional window light ("dramatic natural side lighting from a window creates deep shadows and high contrast"), finish with warm color grading. This formula is the single biggest factor in making AI video look filmed.`,

  // Kling
  'kling-2.6-pro': `Model-specific tips for Kling 2.6 Pro:
- Write prompts "like directions to a scene" — think director's notes.
- Define characters early so the model can map dialogue to speakers.
- Use explicit motion and shot language (wide, medium, close-up).
- Systematic structure: scene → subject → motion → technical detail.
- For UGC realism: "[Specific room with background details]. Dramatic natural side lighting from a window creates deep shadows and high contrast. Warm color grading." — this formula makes footage look filmed.
- Iterative refinement gives best professional results.`,

  'kling-3.0': `Model-specific tips for Kling 3.0:
- Write prompts "like directions to a scene" — think director's notes.
- Define characters early for dialogue mapping.
- Supports native sound in outputs.
- Use explicit motion and shot language.
- Systematic structure: scene → subject → motion → technical detail.
- For UGC realism: named environment + directional window light + warm color grading breaks the "AI-clean" look.`,

  // Wan 2.6
  'wan-2.6': `Model-specific tips for Wan 2.6:
- Three modes: text-to-video, image-to-video, reference-to-video.
- Use global style description + time-coded shot blocks.
- Prompt length limit: ~800 characters — be concise but specific.
- Supports native audio integration.
- Durations 5–15s depending on mode.`,

  // Wan 2.7
  'wan-2.7': `Model-specific tips for Wan 2.7:
- Latest Wan with Thinking Mode (built-in scene planning) and native 1080p output.
- Same prompting structure as 2.6 — global style + time-coded shot blocks.
- Multi-shot scene segmentation works well: \`[0-3s] Wide shot. [3-6s] Close-up.\`
- Supports instruction-based video editing on existing clips.
- Prompt length: ~800 characters max — be specific about subject, motion, lighting.
- Native audio synchronization.`,

  // LTX
  'ltx-2': `Model-specific tips for LTX-2:
- Establish the shot first, then set the scene, then describe action as a sequence.
- Define characters via visual cues (clothing, build, hair) not names.
- Specify camera movement explicitly.
- Put dialogue in quotes and describe audio/SFX.
- Supports longer durations (up to 20s depending on resolution/fps).
- Audio-video foundation model — describe sound design.`,
};

class PromptImprovementService {
  /**
   * Get model-specific prompting tips for the selected model.
   */
  private getModelTips(contentType: string, selectedModel?: string): string {
    if (!selectedModel) return '';

    const tips = contentType === 'video'
      ? VIDEO_MODEL_TIPS[selectedModel]
      : IMAGE_MODEL_TIPS[selectedModel];

    return tips ? `\n\n${tips}` : '';
  }

  /**
   * Improve a prompt using AI
   */
  async improvePrompt(context: PromptImprovementContext): Promise<PromptImprovementResult> {
    const { originalPrompt, contentType, selectedModel, upstreamContext, imageContext, imageUrls, nodeParameters, modelBestPractices } = context;

    let modelTips = this.getModelTips(contentType, selectedModel);
    if (modelBestPractices) {
      modelTips += `\n\nModel best practices (knowledge base):\n${modelBestPractices}`;
    }

    // Append image analysis instructions when images are provided
    const imageAnalysisNote = (imageUrls && imageUrls.length > 0)
      ? `\n\nIMPORTANT: The user has ${imageUrls.length} image(s) connected to this node. These images are provided below for your reference. Analyze them carefully and ensure your improved prompt accurately describes or builds upon what is actually shown in the images — subjects, backgrounds, colors, composition, style, mood, etc. Do NOT invent details that contradict the images.`
      : '';

    // Inject node parameter constraints so the AI respects current settings
    const durationNote = nodeParameters?.duration
      ? `\n\nNODE SETTING — video duration: ${nodeParameters.duration} seconds. The improved prompt must describe a scene that fits within this duration. Do NOT suggest multiple shots or scene breaks unless the user's original prompt explicitly described multiple shots or scenes.`
      : '';

    // Build the system prompt based on content type
    const systemPrompts: Record<string, string> = {
      image: `You are an expert at improving prompts for AI image generation. Make targeted enhancements that amplify the user's intent — do NOT reinvent or transform the prompt.

Guidelines:
- **Preserve intent strictly**: Do NOT swap the subject, setting, or style. If the user wrote "a woman in a red dress", she stays the subject.
- **Enhance what's there**: Add specific visual language — lighting, composition, depth, color, texture — to strengthen what the user described.
- Add constraints only when needed ("clean background", "no watermark") — do NOT add them speculatively.
- Keep the improved prompt concise. Adding length for its own sake degrades model output.
- DO NOT mention aspect ratio, dimensions, or resolution — these are set as node parameters.

**Composition & framing**:
- **Shot type** — open with the shot framing: "A medium shot captures…", "A wide establishing shot of…", "An extreme close-up of…". This anchors the camera-subject relationship.
- **Rule of thirds** — position the subject off-center when it fits: "subject positioned on the left third", "eyes on the upper-third line". Avoids the AI default of centering everything.
- **Leading lines** — use environmental lines to guide the eye: "the corridor's perspective lines draw the eye toward the subject", "the winding road leads into the frame". Especially powerful in urban, architectural, and landscape scenes.
- **Depth layers** — describe foreground, midground, and background separately: "out-of-focus leaves frame the foreground, subject sharp in the midground, city lights blurred in the background". This creates photographic dimensionality.

**Lighting (critical for realism)**:
Lighting is the single biggest factor that separates AI-looking images from photographic ones.
1. **Name the practical light source** — not "good lighting" but the actual source: "store's fluorescent lights casting a cool, pale glow", "warm golden hour backlight", "dramatic natural side lighting from a window", "neon sign glow". Practical light sources feel real.
2. **Shadow quality** — describe what the light creates: "deep shadows and high contrast on her face", "soft diffused shadows", "harsh rim light separating subject from background".
3. **Color grading / film aesthetic** — "warm color grading", "muted greenish-blue tones enhanced by digital color grading", "slightly gritty film texture with desaturated tones", "color correction as if shot at sunset". Specify a look, not just a temperature. This breaks the AI "clean digital" default.
4. **Selective focus** — "sharp focus on the subject, background softly blurred", "shallow depth of field with bokeh". Name what's blurred: "blurred café patrons", "out-of-focus neon signs".
5. **HEX color palette** (optional, powerful) — when the user's prompt implies a specific mood or palette, you may append a HEX VALUES block with 6-12 representative colors to guide the color palette. Only do this if it genuinely serves the prompt.
6. **Mood as closure** — end with an explicit mood statement: "conveying a calm, contemplative mood", "evoking urgency and tension". This anchors the emotional tone.
If the prompt already has strong lighting/composition, enhance it — don't overwrite.${modelTips}${imageAnalysisNote}

Return ONLY the improved prompt, no explanations.`,

      video: `You are an expert at improving prompts for AI video generation. Make minimal, targeted improvements that enhance the user's intent — do not transform or reinvent the prompt.

Guidelines:
- **Preserve intent strictly**: Do NOT add new subjects, settings, or scenarios the user didn't describe.
- **Single shot by default**: Unless the user explicitly mentioned "multiple shots", "several scenes", "cut to", or similar, write as ONE continuous uninterrupted shot. Never add time-codes, shot numbers, or transition language unless the user asked for them.
- **Enhance what's there**: Add cinematic vocabulary — camera angle, motion type (push-in, pan, dolly), shot scale (wide/medium/close), lighting quality — that fit the scene the user described.
- Add atmospheric nuance (color grade, mood, ambient detail) that amplifies the described scene without changing it.
- Keep the improved prompt concise — add depth and specificity, not length.
- DO NOT mention duration, aspect ratio, resolution, or FPS — these are set as node parameters.

**Composition & framing**:
- Open with shot type: "A medium shot captures…", "A wide establishing shot of…". This anchors the visual immediately.
- Use **depth layers** — describe foreground, subject, and background as separate planes: "out-of-focus leaves in the foreground, subject sharp in the midground, city lights blurred in the background".
- Use **leading lines** and environmental geometry when the scene supports it.

**Lighting (critical — makes AI video look filmed)**:
1. **Name the practical light source** — "store's fluorescent lights casting a cool glow", "warm golden hour backlight through blinds", "neon sign glow reflecting off wet pavement". Not "good lighting".
2. **Shadow quality** — "creates deep shadows and high contrast on her face", "soft shadows from diffused overcast light".
3. **Color grading / film aesthetic** — "warm color grading", "muted greenish-blue tones", "slightly gritty film texture", "color correction as if shot at golden hour". Specify a look, not just warm/cool.
4. **Background depth** — "background softly blurred", name what's there: "blurred café patrons", "out-of-focus neon signs".
5. **Mood as closure** — end with the emotional tone: "conveying a calm, contemplative mood", "evoking raw energy".
For UGC-style: named environment + practical light source + warm/desaturated grading + blurred background + slight handheld motion.${durationNote}${modelTips}${imageAnalysisNote}

Return ONLY the improved prompt, no explanations.`,

      text: `You are an expert at improving prompts for AI text generation. Make minimal, targeted improvements that clarify and sharpen the user's intent.

Guidelines:
- Clarify ambiguous instructions with concrete language
- Add context only when it's genuinely missing
- Preserve the user's voice and original framing
- Do NOT add new requirements or constraints the user didn't ask for

Return ONLY the improved prompt, no explanations.`
    };

    // Build the user message with all available context
    let userMessage = `Text to improve: "${originalPrompt}"`;
    
    if (context.surroundingContext) {
      userMessage += `\n\nThis is a SELECTION from a larger prompt. The full prompt is:\n"${context.surroundingContext}"\n\nIMPORTANT: Improve ONLY the selected text above, ensuring it remains consistent with and does not contradict the rest of the prompt.`;
    }
    
    if (upstreamContext) {
      userMessage += `\n\nContext from connected nodes:\n${upstreamContext}`;
    }
    
    if (imageContext) {
      userMessage += `\n\nImage context: ${imageContext}`;
    }
    
    userMessage += '\n\nPlease improve this text while maintaining the original intent and consistency with any surrounding context.';

    try {
      logger.debug('[PromptImprovement] Improving prompt:', { contentType, selectedModel, hasImages: !!(imageUrls && imageUrls.length), originalPrompt: originalPrompt.substring(0, 50) });
      
      const systemPrompt = systemPrompts[contentType] || systemPrompts.text;
      const response = await openAIService.generateResponse(userMessage, systemPrompt, null, imageUrls);

      if (response.content) {
        logger.info('[PromptImprovement] Prompt improved successfully');
        return {
          improvedPrompt: response.content.trim(),
          success: true
        };
      } else {
        throw new Error('No response content from AI');
      }
    } catch (error) {
      logger.error('[PromptImprovement] Failed to improve prompt:', error);
      return {
        improvedPrompt: originalPrompt,
        success: false,
        error: error instanceof Error ? error.message : 'Failed to improve prompt'
      };
    }
  }
}

export const promptImprovementService = new PromptImprovementService();
export default promptImprovementService;
